/// <amd-module name="Artifacts/Ivy/components/IvyPackageAttributes" />
import { PackageDependencies } from "Artifacts/Package/components/package-details-common/PackageDependencies";
import { IProjectAttribute } from "Artifacts/Package/components/package-details-common/PackageDevelopment";
import { PackageProvenance } from "Artifacts/Package/components/package-details-common/PackageProvenance";
import { PackageStats } from "Artifacts/Package/components/package-details-common/PackageStats";
import * as PackageRes from "Artifacts/Package/Resources";
import { IProtocolOverviewProps } from "Artifacts/Root/protocols/types";
import * as React from "react";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { useComponentContext } from "VSS/Platform/Layout";
import {
  IvyXmlConfiguration,
  IvyXmlDescription,
  IvyXmlInfo,
  IvyXmlModule,
  IvyXmlRef,
} from "../Contracts/Ivy";
import { IvyCiConstants } from "../IvyConstants";
import { IvyDevelopment } from "./IvyDevelopment";
import { IvyPackagePeople } from "./IvyPackagePeople";

const IvyAttributes: React.FC<IProtocolOverviewProps> = ({ feed, pkg, version }) => {
  const { pageContext } = useComponentContext();
  const isProvenanceEnabled = isFeatureFlagEnabled(
    pageContext,
    "Packaging.Feed.WebAccess.Provenance",
    false,
  );
  const isMetricsEnabled = isFeatureFlagEnabled(
    pageContext,
    "Packaging.Feed.WebAccess.Metrics",
    false,
  );

  const ivyModule: IvyXmlModule | null =
    version.protocolMetadata && version.protocolMetadata.data
      ? (version.protocolMetadata.data as IvyXmlModule)
      : null;
  const info: IvyXmlInfo | null = ivyModule && ivyModule.info;

  return (
    <section className="package-overview-attributes" aria-label={PackageRes.PackageMetadata}>
      <div className="ivy-overview-attributes-panel">
        <PackageProvenance
          pageContext={pageContext}
          feed={feed}
          packageId={pkg.id}
          versionId={version.id}
          publishDate={new Date(version.publishDate)}
          isProvenanceEnabled={isProvenanceEnabled}
          isProvenanceSupported={false}
        />
        <IvyPackagePeople pageContext={pageContext} authors={info ? info.ivyauthor : null} />
        <IvyDevelopment
          pageContext={pageContext}
          feedId={feed.id}
          normalizedName={pkg.name}
          normalizedVersion={version.normalizedVersion}
          projectConfigurations={_getProjectConfigurations(ivyModule)}
          projectAttributes={_getProjectAttributes(info)}
        />
        {isMetricsEnabled && (
          <PackageStats
            pageContext={pageContext}
            feed={feed}
            package={pkg}
            versionId={version.id}
            isMetricsEnabled={isMetricsEnabled}
          />
        )}
        <PackageDependencies
          pageContext={pageContext}
          feed={feed}
          packageSummary={pkg}
          packageVersion={version}
          dependencyHelperCallback={undefined}
        />
      </div>
    </section>
  );
};

function _getProjectAttributes(info: IvyXmlInfo | null): IProjectAttribute[] | null {
  if (info) {
    const items: IProjectAttribute[] = [];

    items.push({
      iconClass: "PlugConnected",
      text: "index.json",
      url: "",
      onClickCiFeature: IvyCiConstants.ModuleIndexAnchorClicked,
    } as IProjectAttribute);

    if (info.organisation && info.description) {
      info.description.map((d: IvyXmlDescription) => {
        if (d.homepage && d.homepage.length > 0) {
          items.push({
            iconClass: "Globe",
            text: info.organisation,
            url: d.homepage,
            onClickCiFeature: IvyCiConstants.OrganizationLinkClicked,
          } as IProjectAttribute);
        }
      });
    }

    if (info.license && info.license.length > 0) {
      info.license.map((license: IvyXmlRef) => {
        items.push({
          iconClass: "Script",
          text: license.name,
          url: license.url,
          onClickCiFeature: IvyCiConstants.LicenseLinkClicked,
        } as IProjectAttribute);
      });
    }

    if (items.length > 0) {
      return items;
    }
  }

  return null;
}

function _getProjectConfigurations(ivyModule: IvyXmlModule | null): IProjectAttribute[] | null {
  if (ivyModule) {
    const items: IProjectAttribute[] = [];

    if (
      ivyModule.configurations &&
      ivyModule.configurations.conf &&
      ivyModule.configurations.conf.length > 0
    ) {
      ivyModule.configurations.conf.map((conf: IvyXmlConfiguration) => {
        items.push({
          text: conf.name,
        } as IProjectAttribute);
      });
    }

    if (items.length > 0) {
      return items;
    }
  }

  return null;
}

export { IvyAttributes };
